import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { cabecalhoEmpresaHtml, carregarEmpresaRelatorio } from '../utils/cabecalhoRelatorio';
import pedirSenhaAdmin from '../components/common/pedirSenhaAdmin';
import ModalBusca from '../components/financeiro/ModalBuscaContaReceber';
import ModalReceber from '../components/financeiro/ModalReceber';
import ModalBaixaLoteContaReceber from '../components/financeiro/ModalBaixaLoteContaReceber';
import ModalNovoTituloParcelado from '../components/financeiro/ModalNovoTituloParcelado';
import api from '../services/api';
import ModalBoletos from '../components/financeiro/ModalBoletos';
import { gerarReciboPagamento, fecharComEsc, abrirDanfeNfePdf, abrirDanfseOficial, abrirBoleto } from '../utils/impressaoVenda';
import { hojeBR } from '../utils/data';
import s from './PedidosOrcamentos.module.css';
import { prepararWhatsApp, lerModoWhatsApp, salvarModoWhatsApp } from '../utils/abrirWhatsApp';

const fmtMoeda = v => Number(v||0).toLocaleString('pt-BR',{style:'currency',currency:'BRL'});
const fmtDataHora = d => d ? new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
const fmtData  = str => { if (!str) return '—'; return new Date(String(str).slice(0,10)+'T12:00:00').toLocaleDateString('pt-BR'); };
const hoje     = () => hojeBR();

/* ── Autocomplete genérico ── */
function Autocomplete({ lista, value, onChange, placeholder, getLabelFn, getSearchFn, style, inputStyle }) {
  const [texto, setTexto]       = React.useState('');
  const [aberto, setAberto]     = React.useState(false);
  const [filtrado, setFiltrado] = React.useState([]);
  const [destaque, setDestaque] = React.useState(0);
  const wrapRef = React.useRef(null);

  React.useEffect(() => {
    if (!value) { setTexto(''); return; }
    const item = lista.find(i => String(i.id) === String(value));
    if (item) setTexto(getLabelFn(item));
  }, [value, lista]); // eslint-disable-line

  React.useEffect(() => {
    const fn = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setAberto(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  const handleInput = (v) => {
    setTexto(v);
    if (!v) { onChange(''); setFiltrado([]); setAberto(false); return; }
    const q = v.toLowerCase();
    const r = lista.filter(i => getSearchFn(i).toLowerCase().includes(q)).slice(0, 20);
    setFiltrado(r);
    setDestaque(0);
    setAberto(true);
  };

  const handleSelect = (item) => {
    setTexto(getLabelFn(item));
    onChange(String(item.id));
    setAberto(false);
  };

  const handleClear = () => { setTexto(''); onChange(''); setAberto(false); };

  // Enter SELECIONA o item destacado (default: o primeiro da lista filtrada)
  // em vez de só fechar o dropdown — sem isso, digitar o nome e apertar
  // Enter (o fluxo mais natural pra quem não usa mouse) não aplicava o
  // filtro nenhum, mesmo com resultados na lista.
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { setAberto(false); return; }
    if (e.key === 'ArrowDown' && aberto && filtrado.length) {
      e.preventDefault();
      setDestaque(d => Math.min(d + 1, filtrado.length - 1));
      return;
    }
    if (e.key === 'ArrowUp' && aberto && filtrado.length) {
      e.preventDefault();
      setDestaque(d => Math.max(d - 1, 0));
      return;
    }
    if (e.key === 'Enter' && aberto) {
      e.preventDefault();
      e.stopPropagation();
      if (filtrado[destaque]) handleSelect(filtrado[destaque]);
      else setAberto(false);
    }
  };

  const base = { width:'100%', padding:'4px 24px 4px 8px', border:'1px solid #d1d5db',
    borderRadius:4, fontSize:13, height:26, outline:'none' };

  return (
    <div ref={wrapRef} style={{position:'relative', ...style}}>
      <div style={{display:'flex',alignItems:'center',position:'relative'}}>
        <input
          value={texto}
          onChange={e => handleInput(e.target.value)}
          onFocus={() => texto && filtrado.length && setAberto(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          style={{...base, ...inputStyle}}
        />
        {texto && (
          <button onClick={handleClear}
            style={{position:'absolute',right:4,background:'none',border:'none',
              cursor:'pointer',color:'#9ca3af',fontSize:14,lineHeight:1,padding:0}}>×</button>
        )}
      </div>
      {aberto && filtrado.length > 0 && (
        <div style={{position:'absolute',top:'100%',left:0,right:0,zIndex:200,
          background:'#fff',border:'1px solid #d1d5db',borderRadius:4,
          boxShadow:'0 4px 12px rgba(0,0,0,0.15)',maxHeight:220,overflowY:'auto'}}>
          {filtrado.map((item, i) => (
            <div key={item.id} onMouseDown={() => handleSelect(item)} onMouseEnter={() => setDestaque(i)}
              style={{padding:'6px 10px',cursor:'pointer',fontSize:13,borderBottom:'1px solid #f3f4f6',
                background: i===destaque ? '#EBF5FF' : '#fff'}}>
              {getLabelFn(item)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const STATUS_LABEL = {
  aberto:    { txt: 'Aberto',    cls: s.badgeAberto    },
  vencido:   { txt: 'Vencido',   cls: s.badgeCancelado },
  recebido:  { txt: 'Recebido',  cls: s.badgeAprovado  },
  cancelado: { txt: 'Cancelado', cls: s.badgeCancelado },
};

const statusEfetivo = (cr) => {
  if (cr.status === 'recebido' || cr.status === 'cancelado') return cr.status;
  if (cr.data_vencimento && cr.data_vencimento < hoje()) return 'vencido';
  return 'aberto';
};

const COR_STATUS = {
  aberto:    '#1565c0',
  vencido:   '#b71c1c',
  recebido:  '#2e7d32',
  cancelado: '#888',
};

// Conta aberta com recebimento em cartão registrado = aguardando Baixa de Cartões
const ehCartaoPendente = (c) =>
  c && c.status === 'aberto' && (c.forma_recebimento || '').toLowerCase().includes('cart');

const FORM_VAZIO = {
  id: null, descricao: '', valor: '', numero_documento: '',
  parcela: '', total_parcelas: '',
  data_emissao: hoje(), data_vencimento: '', data_recebimento: '',
  cliente_id: '', cliente_nome: '', cliente_codigo: '',
  conta_id: '', plano_conta_id: '', observacoes: '', status: 'aberto',
};

/* ════════════════════════════════════════
   Tela principal — Contas a Receber
════════════════════════════════════════ */
export default function ContasReceber() {
  const { usuario } = useAuth();
  const [orgData, setOrgData]       = useState(null);
  const [imprimindoRecibo, setImprimindoRecibo] = useState(false);
  const [lista, setLista]           = useState([]);
  const [form, setForm]             = useState(FORM_VAZIO);
  const [modo, setModo]             = useState('nav');   // nav | novo | editar
  const [posicao, setPosicao]       = useState(0);
  const [total, setTotal]           = useState(0);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando]     = useState(false);
  const [erroMsg, setErroMsg]       = useState('');
  const [resumo, setResumo]         = useState({});

  // Filtros da lista
  // tipo_data escolhe se venc_de/venc_ate (mesmos campos da tela) filtram
  // por vencimento (padrão, comportamento de sempre) ou emissão — só vale
  // pra aberto/vencido/todos; com status=recebido o par já tem significado
  // fixo (data de recebimento) e o seletor fica oculto.
  const [filtros, setFiltros]       = useState({ status:'aberto', busca:'', venc_de:'2000-01-01', venc_ate:'2099-12-31', tipo_data:'vencimento', cliente_id:'', mostrar_cartoes:false, consolidar:false, so_nao_enviados:false });
  // Campo de data com texto que não é data de verdade (ex.: 31/09) — o input
  // devolve value='' nesse caso, o que viraria "sem limite" no filtro.
  const [datasInvalidas, setDatasInvalidas] = useState({ de:false, ate:false });
  const [chaveDatas, setChaveDatas] = useState(0);
  const [outrasLojas, setOutrasLojas] = useState([]);
  const [selId, setSelId]           = useState(null); // eslint-disable-line

  // Baixa em lote — F12 marca/desmarca o registro atual e pula pro próximo
  const [marcados, setMarcados]         = useState(new Set());
  const [modalBaixaLote, setModalBaixaLote] = useState(false);

  // Marcar (F12 ou checkbox) um título que veio de uma Baixa em Lote marca
  // junto os demais títulos do MESMO lote (mesmo baixa_lote_id) já visíveis
  // na grade — poupa ter que re-identificar um por um pra corrigir uma
  // baixa em lote inteira. Desmarcar nunca arrasta os outros: cada um só
  // sai da seleção se o usuário desmarcar ele mesmo.
  const marcar = (id, ligar) => {
    setMarcados(prev => {
      const next = new Set(prev);
      if (!ligar) { next.delete(id); return next; }
      next.add(id);
      const item = lista.find(c => c.id === id);
      if (item?.baixa_lote_id) {
        lista.forEach(c => { if (c.baixa_lote_id === item.baixa_lote_id) next.add(c.id); });
      }
      return next;
    });
  };

  // Mesma regra de elegibilidade usada no checkbox de cada linha — extraída
  // aqui pra "marcar todos" nunca marcar um título que a linha não deixaria.
  const elegivelParaMarcar = (c) => !c.outra_loja && (
    (c.status === 'aberto' && !ehCartaoPendente(c)) || (c.status === 'recebido' && !c.baixa_id));

  // Botão "Marcar/desmarcar todos" — mesma função do F12, só que na lista
  // inteira de uma vez. Alterna: se tudo elegível visível já está marcado,
  // desmarca tudo; senão, marca todo mundo elegível visível.
  const elegiveisVisiveis = useMemo(() => lista.filter(elegivelParaMarcar), [lista]);
  const todosMarcados = elegiveisVisiveis.length > 0 && elegiveisVisiveis.every(c => marcados.has(c.id));
  const marcarTodos = () => {
    setMarcados(todosMarcados ? new Set() : new Set(elegiveisVisiveis.map(c => c.id)));
  };

  // Clientes, contas e plano de contas
  const [clientes, setClientes]         = useState([]);
  const [contasFinanc, setContasFinanc] = useState([]);
  const [planoContas,  setPlanoContas]  = useState([]);
  const [bandeiras, setBandeiras]       = useState([]);
  const [creditoCli, setCreditoCli]     = useState(0);
  const [modalCredito, setModalCredito] = useState(false);
  const [modalNovo, setModalNovo]       = useState(false);
  const [histCred, setHistCred]         = useState(null);

  // Modais
  const [modalBusca, setModalBusca]     = useState(false);
  const [modalBoletos, setModalBoletos] = useState(false);
  const [modalReceber, setModalReceber] = useState(false);

  // Doc. fiscal (NF-e/NFC-e/NFS-e) e boleto do título selecionado — mesmos
  // botões que o F7 (Buscar Movimentação de Caixa, PDV) já oferece pra uma
  // venda, só que aqui a partir do título em Contas a Receber. `docFiscalCr`
  // vem de um endpoint próprio (GET .../documentos-fiscais, só exige a
  // permissão de financeiro — não a de vendas), mas segue a mesma
  // prioridade que o F7 usa (NF-e/NFC-e > NFS-e quando os dois existem —
  // ver vendasController.listar/contasReceberController.documentosFiscaisDaConta).
  const [docFiscalCr, setDocFiscalCr] = useState(null);
  const [boletoCr, setBoletoCr]       = useState(null);
  useEffect(() => {
    let cancelado = false;
    setDocFiscalCr(null);
    setBoletoCr(null);
    if (!form.id) return;
    api.get(`/financeiro/contas-receber/${form.id}/documentos-fiscais`)
      .then(({ data }) => { if (!cancelado) setDocFiscalCr(data || null); })
      .catch(() => {});
    api.get('/financeiro/boletos', { params: { conta_receber_id: form.id } })
      .then(({ data }) => { if (!cancelado) setBoletoCr(data?.[0] || null); })
      .catch(() => {});
    return () => { cancelado = true; };
  }, [form.id]);

  // "Enviar WhatsApp"/"Enviar E-mail" — mesma ideia do F7 (ver
  // vendasController.compartilhar/enviarEmailDocumentos), aqui a partir do
  // título selecionado. WhatsApp é só um link (wa.me) pronto com o texto e
  // os links do boleto/nota — o usuário confere e aperta Enviar lá dentro,
  // não é um disparo automático (não existe integração paga com a API
  // oficial do WhatsApp Business).
  const handleEnviarWhatsapp = async () => {
    // Aberta ANTES do await — depois do await o navegador não conta mais
    // como "resposta direta a um clique" e bloqueia o pop-up (mesmo motivo
    // documentado em impressaoVenda.js#abrirDanfseOficial).
    const alvo = prepararWhatsApp();
    try {
      const { data } = await api.get(`/financeiro/contas-receber/${form.id}/compartilhar`);
      if (data.whatsapp_url) { alvo.abrir(data.whatsapp_url); carregarLista(); return; }
      alvo.cancelar();
      alert(data.whatsapp_motivo_indisponivel || 'Não foi possível montar o link do WhatsApp.');
    } catch (err) {
      alvo.cancelar();
      alert(err.response?.data?.erro || 'Erro ao montar o link do WhatsApp.');
    }
  };

  // ── Envio em lote (títulos marcados) ──
  // E-mail: o servidor manda um por um. WhatsApp: fila — cada clique abre uma
  // conversa (o navegador bloqueia abrir várias de uma vez e o "Enviar" é
  // apertado pela pessoa dentro do WhatsApp).
  const [loteEmail, setLoteEmail] = useState(null);   // { enviando, resultados }
  const [filaWhats, setFilaWhats] = useState(null);   // [{ id, cliente, valor, estado, msg }]
  const [modoWhats, setModoWhats] = useState(() => lerModoWhatsApp() || '');
  const enviarEmailMarcados = async () => {
    const ids = titulosMarcados.map(c => c.id);
    if (!ids.length) return;
    if (!window.confirm(`Enviar por e-mail o boleto/nota de ${ids.length} título(s) marcado(s)?\n\nCada um vai para o e-mail cadastrado do próprio cliente.`)) return;
    setLoteEmail({ enviando: true, resultados: [] });
    try {
      const { data } = await api.post('/financeiro/contas-receber/enviar-email-lote', { ids });
      setLoteEmail({ enviando: false, resultados: data.resultados || [] });
    } catch (err) {
      setLoteEmail({ enviando: false, resultados: err.response?.data?.resultados || [], erro: err.response?.data?.erro || 'Erro ao enviar os e-mails.' });
    } finally { carregarLista(); }
  };
  const abrirFilaWhatsapp = () => setFilaWhats(titulosMarcados.map(c => ({
    id: c.id, cliente: c.cliente_nome, valor: c.valor, vencimento: c.data_vencimento,
    estado: c.whatsapp_em ? 'ja' : 'pendente', msg: c.whatsapp_em ? `já aberto em ${fmtDataHora(c.whatsapp_em)}` : '',
  })));
  const abrirWhatsDaFila = async (item) => {
    const alvo = prepararWhatsApp(); // antes do await (bloqueio de pop-up)
    setModoWhats(lerModoWhatsApp());
    const atualizar = (estado, msg) => setFilaWhats(f => f.map(x => x.id === item.id ? { ...x, estado, msg } : x));
    try {
      const { data } = await api.get(`/financeiro/contas-receber/${item.id}/compartilhar`);
      if (data.whatsapp_url) { alvo.abrir(data.whatsapp_url); atualizar('aberto', 'aberto agora'); return; }
      alvo.cancelar(); atualizar('erro', data.whatsapp_motivo_indisponivel || 'sem link do WhatsApp');
    } catch (err) {
      alvo.cancelar(); atualizar('erro', err.response?.data?.erro || 'erro ao montar o link');
    }
  };

  const [enviandoEmailCr, setEnviandoEmailCr] = useState(false);
  const handleEnviarEmailCr = async () => {
    const destino = window.prompt('E-mail de destino (deixe em branco para usar o e-mail cadastrado do cliente):', '');
    if (destino === null) return;
    setEnviandoEmailCr(true);
    try {
      const { data } = await api.post(`/financeiro/contas-receber/${form.id}/enviar-email`,
        destino.trim() ? { email: destino.trim() } : {});
      alert(`✅ E-mail enviado para ${data.enviado_para}.\nAnexo(s): ${data.anexos.join(', ')}`);
      carregarLista();
    } catch (err) {
      alert('❌ ' + (err.response?.data?.erro || 'Erro ao enviar o e-mail.'));
    } finally {
      setEnviandoEmailCr(false);
    }
  };

  const editando = modo !== 'nav';

  /* ── Refs e navegação por Enter ── */
  const formRef     = useRef(null);
  const primeiroRef = useRef(null);
  const salvarRef   = useRef(null);

  useEffect(() => { salvarRef.current = handleSalvar; });

  useEffect(() => {
    if (modo === 'novo' || modo === 'editar') {
      setTimeout(() => primeiroRef.current?.focus(), 80);
    }
  }, [modo]);

  const navEnter = useCallback((e) => {
    if (e.key !== 'Enter' || e.shiftKey || e.ctrlKey || e.altKey) return;
    const campos = Array.from(
      formRef.current?.querySelectorAll('input:not([readonly]):not([disabled]), select:not([disabled])') || []
    );
    const idx = campos.indexOf(e.target);
    if (idx >= 0 && idx < campos.length - 1) {
      e.preventDefault();
      campos[idx + 1].focus();
    } else if (idx === campos.length - 1) {
      e.preventDefault();
      salvarRef.current?.();
    }
  }, []);

  /* ── Carregar dados auxiliares ── */
  useEffect(() => {
    // Sem "todos=true" a API pagina em 20 — os autocompletes de Cliente
    // desta tela nunca achavam quem não estivesse nos 20 primeiros código
    // (ex.: cliente #49) porque a lista carregada já vinha incompleta.
    api.get('/clientes', { params: { todos: 'true' } }).then(r=>setClientes(r.data||[])).catch(()=>{});
    api.get('/financeiro/contas').then(r=>setContasFinanc(r.data||[])).catch(()=>{});
    api.get('/financeiro/bandeiras').then(r=>setBandeiras((r.data||[]).filter(b=>b.ativo))).catch(()=>{});
    api.get('/financeiro/plano-contas/analiticas', { params: { tipo: 'receita' } })
       .then(r=>setPlanoContas(r.data||[])).catch(()=>{});
    if (usuario?.organizacao_id)
      api.get(`/organizacoes/${usuario.organizacao_id}`).then(r=>setOrgData(r.data)).catch(()=>{});
    carregarResumo();
  }, []); // eslint-disable-line

  /* ── Recibo de pagamento (só título recebido) ── */
  const imprimirRecibo = async (largura) => {
    if (!form.id) return;
    setImprimindoRecibo(true);
    try {
      const { data } = await api.get(`/financeiro/contas-receber/${form.id}/recibo`);
      await gerarReciboPagamento(data, orgData || {}, largura);
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao gerar o recibo.');
    } finally {
      setImprimindoRecibo(false);
    }
  };

  const carregarResumo = async () => {
    try { const {data}=await api.get('/financeiro/contas-receber/resumo'); setResumo(data||{}); } catch {}
  };

  // Crédito disponível do cliente da conta selecionada
  useEffect(() => {
    if (!form.cliente_id || form.cliente_id === 1) { setCreditoCli(0); return; }
    api.get('/financeiro/creditos-cliente', { params: { cliente_id: form.cliente_id } })
      .then(r => setCreditoCli(r.data?.total || 0))
      .catch(() => setCreditoCli(0));
  }, [form.cliente_id, form.status, modalReceber]);

  /* ── Carregar lista ── */
  const carregarLista = useCallback(async (f=filtros) => {
    setCarregando(true);
    try {
      // venc_de/venc_ate (os mesmos 2 campos da tela) viram emis_de/emis_ate
      // quando o usuário escolhe filtrar por emissão — o backend já suporta
      // os dois pares independentes, só nunca tinha UI pra emissão fora do
      // status=recebido (que usa venc_de/venc_ate com significado próprio,
      // fixo em data de recebimento — nunca remapeado aqui).
      const { tipo_data, venc_de, venc_ate, consolidar, so_nao_enviados, ...resto } = f;
      const base = { ...(consolidar ? { ...resto, consolidar: 1 } : resto), ...(so_nao_enviados ? { envio: 'nao_enviado' } : {}) };
      const params = tipo_data === 'emissao' && f.status !== 'recebido'
        ? { ...base, emis_de: venc_de, emis_ate: venc_ate }
        : { ...base, venc_de, venc_ate };
      const { data } = await api.get('/financeiro/contas-receber', { params });
      // Cartões aguardando baixa ficam ocultos, salvo se o checkbox estiver marcado
      const visiveis = f.mostrar_cartoes ? data : data.filter(c => !ehCartaoPendente(c));
      setLista(visiveis);
      setTotal(visiveis.length);
      return visiveis;
    } catch { return []; }
    finally { setCarregando(false); }
  }, [filtros]);

  useEffect(() => { carregarLista(); }, []); // eslint-disable-line

  useEffect(() => {
    if (!filtros.cliente_id || filtros.consolidar) { setOutrasLojas([]); return; }
    api.get('/financeiro/contas-receber/outras-lojas', { params: { cliente_id: filtros.cliente_id } })
      .then(r => setOutrasLojas(r.data || [])).catch(() => setOutrasLojas([]));
  }, [filtros.cliente_id, filtros.consolidar]);

  // Soma do que está sendo exibido na grade agora — com filtro de cliente
  // aplicado, é o total de débitos daquele cliente (respeitando o status
  // selecionado: aberto, vencido, recebido ou todos). Com status=recebido
  // soma o valor PAGO (líquido, já com desconto/juros), coerente com a
  // coluna "Valor Pago" exibida na grade nesse caso.
  const totalListaAtual = useMemo(
    () => lista.reduce((s, c) => s + parseFloat((filtros.status==='recebido' ? (c.valor_liquido_recebido ?? c.valor) : c.valor) || 0), 0),
    [lista, filtros.status]
  );
  const clienteFiltrado = filtros.cliente_id
    ? clientes.find(c => c.id === filtros.cliente_id)
    : null;

  /* ── Abrir registro ── */
  const abrirConta = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/financeiro/contas-receber/${id}`);
      setForm({
        ...FORM_VAZIO, ...data,
        valor:            data.valor || '',
        parcela:          data.parcela || '',
        total_parcelas:   data.total_parcelas || '',
        data_emissao:     data.data_emissao?.slice(0,10) || hoje(),
        data_vencimento:  data.data_vencimento?.slice(0,10) || '',
        data_recebimento: data.data_recebimento?.slice(0,10) || '',
        cliente_id:       data.cliente_id || '',
        conta_id:         data.conta_id || '',
        plano_conta_id:   data.plano_conta_id || '',
      });
      setModo('nav');
      setErroMsg('');
    } catch { setErroMsg('Erro ao carregar conta.'); }
  }, []);

  // Título de outra loja (Consolidar lojas) é só consulta: as ações do
  // formulário (receber, alterar, excluir) valem para a loja logada.
  const abrirLinha = useCallback(async (c) => {
    if (!c) return;
    if (c.outra_loja) {
      setForm(FORM_VAZIO); setModo('nav');
      setErroMsg(`Título nº ${c.id} é da loja "${c.loja_nome}". Para receber ou alterar, entre nessa loja.`);
      return;
    }
    await abrirConta(c.id);
  }, [abrirConta]);
  const abrirPrimeiro = async (arr) => {
    const i = arr.findIndex(c => !c.outra_loja);
    if (i >= 0) { setPosicao(i); await abrirConta(arr[i].id); }
    else { setForm(FORM_VAZIO); setModo('nav'); }
  };

  /* ── Navegação ── */
  const navPara = useCallback(async (idx, arr=lista) => {
    if (!arr.length) return;
    const i = Math.max(0, Math.min(idx, arr.length-1));
    setPosicao(i);
    await abrirLinha(arr[i]);
  }, [lista, abrirLinha]);

  /* ── Novo ── */
  /* Novo: lançamento manual em modal com parcelamento (mesma dinâmica do CP) */
  const handleNovo = () => { if (!editando) setModalNovo(true); };

  const handleNovoSalvo = async (novoId) => {
    setModalNovo(false);
    const arr = await carregarLista();
    const idx = arr.findIndex(x => x.id === novoId);
    if (idx >= 0) { setPosicao(idx); await abrirConta(novoId); }
    carregarResumo();
  };

  /* F4 abre o lançamento manual (paridade com Contas a Pagar) */
  useEffect(() => {
    const h = e => {
      if (e.key !== 'F4') return;
      if (editando || modalNovo || modalReceber || modalBusca || modalBoletos || modalCredito) return;
      e.preventDefault();
      setModalNovo(true);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  });

  /* ── Alterar ── */
  const handleAlterar = () => { setModo('editar'); setErroMsg(''); };

  /* ── Cancelar ── */
  const handleCancelar = () => {
    setErroMsg('');
    if (form.id) { setModo('nav'); abrirConta(form.id); }
    else { setForm(FORM_VAZIO); setModo('nav'); }
  };

  /* ── Salvar ── */
  const handleSalvar = async () => {
    if (!form.descricao.trim()) return setErroMsg('Descrição é obrigatória.');
    if (!form.valor || parseFloat(form.valor)<=0) return setErroMsg('Informe um valor válido.');
    if (!form.data_vencimento) return setErroMsg('Data de vencimento é obrigatória.');
    setSalvando(true); setErroMsg('');
    try {
      const payload = {
        descricao:        form.descricao.trim(),
        valor:            parseFloat(form.valor),
        numero_documento: form.numero_documento || null,
        parcela:          form.parcela ? parseInt(form.parcela) : null,
        total_parcelas:   form.total_parcelas ? parseInt(form.total_parcelas) : null,
        data_emissao:     form.data_emissao || hoje(),
        data_vencimento:  form.data_vencimento,
        data_recebimento: form.data_recebimento || null,
        cliente_id:       form.cliente_id || null,
        conta_id:         form.conta_id || null,
        plano_conta_id:   form.plano_conta_id || null,
        observacoes:      form.observacoes || null,
        status:           form.status || 'aberto',
      };
      if (form.id) {
        await api.put(`/financeiro/contas-receber/${form.id}`, payload);
      } else {
        const { data } = await api.post('/financeiro/contas-receber', payload);
        payload.id = data.id;
        setForm(f=>({...f, id: data.id}));
      }
      const arr = await carregarLista();
      const idx = arr.findIndex(x => x.id === (form.id || payload.id));
      if (idx>=0) { setPosicao(idx); await abrirConta(arr[idx].id); }
      setModo('nav');
      carregarResumo();
    } catch (err) {
      setErroMsg(err.response?.data?.erro || 'Erro ao salvar.');
    } finally { setSalvando(false); }
  };

  /* ── Receber ── */
  const handleReceber = async (dados) => {
    try {
      const { data } = await api.post(`/financeiro/contas-receber/${form.id}/receber`, dados);
      setModalReceber(false);
      const arr = await carregarLista();
      carregarResumo();
      if (data.cartao_pendente) {
        alert('Recebimento em cartão registrado! O valor está aguardando na Baixa de Cartões — a conta será liquidada quando a baixa for realizada.');
        await abrirConta(form.id);
      } else if (data.parcial && data.restante) {
        const idx = arr.findIndex(x => x.id === data.restante.id);
        if (idx >= 0) { setPosicao(idx); await abrirConta(data.restante.id); }
      } else {
        await abrirConta(form.id);
      }
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao confirmar recebimento.');
    }
  };

  /* ── Estornar recebimento ── */
  const handleEstornarRecebimento = async () => {
    if (!form.id) return;
    if (!window.confirm(`Estornar o recebimento de "${form.descricao}"?\n\nA conta voltará para ABERTO e a entrada na conta financeira será removida (saldo revertido).`)) return;
    const senha = await pedirSenhaAdmin();
    if (senha === null) return;
    try {
      await api.post(`/financeiro/contas-receber/${form.id}/estornar-recebimento`, { senha_admin: senha });
      await carregarLista();
      carregarResumo();
      await abrirConta(form.id);
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao estornar recebimento.');
    }
  };

  /* ── Excluir ── */
  const handleExcluir = async () => {
    if (!form.id) return;
    if (!window.confirm(`Excluir "${form.descricao}"?`)) return;
    try {
      await api.delete(`/financeiro/contas-receber/${form.id}`);
      const arr = await carregarLista();
      await abrirPrimeiro(arr);
      carregarResumo();
    } catch (err) {
      setErroMsg(err.response?.data?.erro || 'Erro ao excluir.');
    }
  };

  /* ── Filtro ── */
  const handleFiltro = async (patch, invalidas = datasInvalidas) => {
    const nf = {...filtros,...patch};
    setFiltros(nf);
    if (invalidas.de || invalidas.ate) { setLista([]); setTotal(0); setForm(FORM_VAZIO); return; }
    const arr = await carregarLista(nf);
    await abrirPrimeiro(arr);
  };
  const mudarData = (campo, e) => {
    const inv = { ...datasInvalidas, [campo === 'venc_de' ? 'de' : 'ate']: e.target.value === '' && e.target.validity?.badInput };
    setDatasInvalidas(inv);
    handleFiltro({ [campo]: e.target.value }, inv);
  };

  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const stEf = form.id ? statusEfetivo(form) : 'aberto';
  const stLabel = STATUS_LABEL[stEf] || STATUS_LABEL.aberto;

  const cartaoPendente = ehCartaoPendente(form);
  const podeReceber = form.id && stEf !== 'recebido' && stEf !== 'cancelado' && !editando && !cartaoPendente;

  /* ── Atalhos de teclado: Ctrl+G foca a grade, ↑/↓ navegam, Ctrl+R recebe ── */
  const gradeRef = useRef(null);
  // Linha ativa segue a posição (↑/↓ e F12, que também navega depois de
  // marcar) — sem isso, navegar/marcar passava da área visível da grade
  // sem o scroll acompanhar (mesmo padrão já usado em BaixaCartoes.jsx).
  const rowRefs = useRef({});
  useEffect(() => {
    rowRefs.current[posicao]?.scrollIntoView?.({ block: 'nearest' });
  }, [posicao, lista]);
  useEffect(() => {
    const h = e => {
      if (editando || modalReceber) return;
      if (e.ctrlKey && e.key.toLowerCase() === 'g') {
        e.preventDefault();
        gradeRef.current?.focus();
        if (lista.length && posicao < 0) navPara(0);
        return;
      }
      if (e.ctrlKey && e.key.toLowerCase() === 'r') {
        e.preventDefault();
        if (podeReceber) setModalReceber(true);
        return;
      }
      const tag = document.activeElement?.tagName;
      if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') &&
          !['INPUT', 'SELECT', 'TEXTAREA'].includes(tag)) {
        if (!lista.length) return;
        e.preventDefault();
        const n = e.key === 'ArrowDown'
          ? Math.min(posicao + 1, lista.length - 1)
          : Math.max(posicao - 1, 0);
        if (n !== posicao) navPara(n);
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  });

  /* ── F12: marca/desmarca o registro atual pra baixa em lote e pula pro
     próximo (marcando ou desmarcando — sempre avança) ── */
  useEffect(() => {
    const h = e => {
      if (e.key !== 'F12') return;
      if (editando || modalReceber || modalBaixaLote || modalBusca || modalBoletos || modalCredito || modalNovo) return;
      const tag = document.activeElement?.tagName;
      if (['INPUT', 'SELECT', 'TEXTAREA'].includes(tag)) return;
      if (!lista.length || posicao < 0 || posicao >= lista.length) return;
      e.preventDefault();
      const id = lista[posicao].id;
      if (!lista[posicao].outra_loja) marcar(id, !marcados.has(id));
      const n = Math.min(posicao + 1, lista.length - 1);
      if (n !== posicao) navPara(n);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  });

  /* ── Baixar em lote ── */
  const titulosMarcados = lista.filter(c => marcados.has(c.id));
  // A marcação (F12/checkbox) é a mesma pros dois sentidos — o que muda é o
  // status de cada título marcado: aberto → elegível pra BAIXAR; recebido
  // (sem ter passado por Baixa de Cartões) → elegível pra ESTORNAR. Com uma
  // seleção mista (ex.: filtro "Todos"), os dois botões aparecem juntos, cada
  // um agindo só no seu subconjunto.
  const titulosParaBaixa   = titulosMarcados.filter(c => c.status === 'aberto' && !ehCartaoPendente(c));
  const titulosParaEstorno = titulosMarcados.filter(c => c.status === 'recebido' && !c.baixa_id);
  const handleBaixarLote = async (dados) => {
    try {
      const { data } = await api.post('/financeiro/contas-receber/baixar-lote', dados);
      setModalBaixaLote(false);
      setMarcados(new Set());
      await carregarLista();
      carregarResumo();
      alert(`${data.quantidade} título(s) baixado(s) — total ${fmtMoeda(data.total)}.`);
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao baixar em lote.');
    }
  };

  /* ── Estornar em lote — corrige baixas feitas em lote por engano (achar
     pelos filtros Recebido + período de recebimento + cliente, marcar os
     certos com F12/checkbox e estornar de uma vez). Best-effort: um título
     que não puder ser estornado não trava os demais, o backend reporta os
     dois grupos separadamente. ── */
  const handleEstornarLote = async () => {
    if (!titulosParaEstorno.length) return;
    if (!window.confirm(
      `Estornar o recebimento de ${titulosParaEstorno.length} título(s) marcado(s)?\n\n` +
      `Cada conta volta para ABERTO e a entrada na conta financeira é revertida (saldo revertido). ` +
      `Um título que não puder ser estornado (ex.: tem uma "restante" já recebida) aparece no aviso ao final, sem travar os demais.`
    )) return;
    const senha = await pedirSenhaAdmin();
    if (senha === null) return;
    try {
      const { data } = await api.post('/financeiro/contas-receber/estornar-lote', {
        ids: titulosParaEstorno.map(c => c.id), senha_admin: senha,
      });
      setMarcados(new Set());
      await carregarLista();
      carregarResumo();
      const msg = [`${data.sucesso.length} título(s) estornado(s).`];
      if (data.falhas.length) {
        msg.push(`${data.falhas.length} não puderam ser estornados:`);
        data.falhas.forEach(f => msg.push(`— ${f.erro}`));
      }
      alert(msg.join('\n'));
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao estornar em lote.');
    }
  };

  /* ── Imprimir lista dos documentos marcados p/ baixa em lote ── */
  const imprimirMarcados = async () => {
    if (!titulosMarcados.length) return;
    // Aberta antes do await: depois da chamada de rede o navegador bloqueia o pop-up.
    const win = window.open('', '_blank', 'width=850,height=750');
    if (!win) { alert('Permita pop-ups para imprimir.'); return; }
    const empresaHtml = cabecalhoEmpresaHtml(await carregarEmpresaRelatorio());
    const total = titulosMarcados.reduce((a, c) => a + parseFloat(c.valor || 0), 0);
    const th = 'padding:5px 8px;border-bottom:2px solid #1e3a5f;font-size:9.5pt;color:#1e3a5f;text-align:left;';
    const td = 'padding:4px 8px;border-bottom:1px solid #e5e7eb;font-size:10pt;';
    const linhas = titulosMarcados.map((c, i) => `
      <tr style="background:${i % 2 === 0 ? '#fff' : '#f8f9fc'};">
        <td style="${td}">${c.id}</td>
        <td style="${td}">${c.descricao || '—'}</td>
        <td style="${td}text-align:center;">${c.cliente_codigo ?? '—'}</td>
        <td style="${td}">${c.cliente_nome || '—'}</td>
        <td style="${td}text-align:center;">${fmtData(c.data_emissao)}</td>
        <td style="${td}text-align:center;">${fmtData(c.data_vencimento)}</td>
        <td style="${td}text-align:right;font-weight:600;">${fmtMoeda(c.valor)}</td>
      </tr>`).join('');

    const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8">
  <title>Documentos Marcados — Baixa em Lote</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    @page { size: A4; margin: 14mm 12mm; }
    body { font-family: Arial, sans-serif; color: #1a1a1a; background: #fff; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style>
</head>
<body>
  ${empresaHtml}
  <div style="border-bottom:3px solid #1e3a5f;padding-bottom:10px;margin-bottom:14px;
              display:flex;justify-content:space-between;align-items:flex-end;">
    <div>
      <div style="font-size:16pt;font-weight:bold;color:#1e3a5f;">Documentos Marcados — Baixa em Lote</div>
      <div style="font-size:10pt;color:#555;margin-top:2px;">Contas a Receber</div>
    </div>
    <div style="text-align:right;font-size:10pt;color:#555;">
      <div>${titulosMarcados.length} documento(s)</div>
      <div>Emitido em ${new Date().toLocaleString('pt-BR')}</div>
    </div>
  </div>
  <table style="width:100%;border-collapse:collapse;">
    <thead>
      <tr>
        <th style="${th}">Cód.</th>
        <th style="${th}">Descrição</th>
        <th style="${th}text-align:center;">Cód. Cliente</th>
        <th style="${th}">Cliente</th>
        <th style="${th}text-align:center;">Emissão</th>
        <th style="${th}text-align:center;">Vencimento</th>
        <th style="${th}text-align:right;">Valor</th>
      </tr>
    </thead>
    <tbody>${linhas}</tbody>
    <tfoot>
      <tr style="background:#1e3a5f;color:#fff;font-weight:bold;">
        <td style="padding:6px 8px;font-size:10.5pt;" colspan="6">Total</td>
        <td style="padding:6px 8px;font-size:10.5pt;text-align:right;">${fmtMoeda(total)}</td>
      </tr>
    </tfoot>
  </table>
</body>
</html>`;

    win.document.write(html);
    win.document.close();
    fecharComEsc(win);
    win.focus();
    setTimeout(() => win.print(), 400);
  };

  const podeCancelarStatus = form.id && stEf !== 'cancelado' && stEf !== 'recebido' && !editando;

  /* ── Estilos de edição destacada ── */
  const iSt = editando
    ? { fontSize: 14, height: 30, padding: '4px 8px', borderColor: '#1976d2', background: '#fff', fontWeight: 500 }
    : {};
  const lSt = editando ? { fontSize: 12, fontWeight: 700, color: '#1565c0' } : {};

  return (
    <div className={s.erpWrap}>
      {/* ── Barra de título ── */}
      <div className={s.titleBar}>
        <span>💰</span> Contas a Receber
      </div>

      {/* ── Painel de resumo (cards) ── */}
      <div style={{display:'flex',gap:12,padding:'10px 12px',background:'#e8eaf0',borderBottom:'1px solid #c0c8d8',flexWrap:'wrap'}}>
        {[
          { rot: 'A receber',       val: resumo.total_aberto,       extra: `${resumo.qtd_aberto||0} conta(s)`, cor: '#1565c0', bg: '#eff6ff', bd: '#93c5fd' },
          { rot: 'Vencido',         val: resumo.total_vencido,      extra: `${resumo.qtd_vencido||0} conta(s)`, cor: '#b71c1c', bg: '#fef2f2', bd: '#fca5a5' },
          { rot: 'Recebido no mês', val: resumo.total_recebido_mes, extra: null, cor: '#2e7d32', bg: '#f0fdf4', bd: '#86efac' },
        ].map(c => (
          <div key={c.rot} style={{background:c.bg,border:`1px solid ${c.bd}`,borderRadius:6,
            padding:'8px 16px',minWidth:170}}>
            <div style={{fontSize:12,fontWeight:700,color:c.cor,textTransform:'uppercase'}}>{c.rot}</div>
            <div style={{fontSize:18,fontWeight:700,color:c.cor}}>{fmtMoeda(c.val)}</div>
            {c.extra && <div style={{fontSize:12,color:'#666'}}>{c.extra}</div>}
          </div>
        ))}
        {form.cliente_id && form.cliente_id !== 1 && (
          <div style={{background:'#f0fdf4',border:'1px solid #86efac',borderRadius:6,padding:'8px 16px',minWidth:190}}
            title="Crédito gerado por devolução — use na janela Receber (forma: Crédito do Cliente)">
            <div style={{fontSize:12,fontWeight:700,color:'#166534',textTransform:'uppercase'}}>💳 Crédito do cliente</div>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <span style={{fontSize:18,fontWeight:700,color:'#166534'}}>{fmtMoeda(creditoCli)}</span>
              <button className={s.btnErp} style={{height:24,fontSize:12}}
                title="Histórico do crédito: qual devolução gerou e em quais baixas foi usado"
                onClick={async () => {
                  setHistCred(null); setModalCredito(true);
                  try {
                    const { data } = await api.get('/financeiro/creditos-cliente/historico',
                      { params: { cliente_id: form.cliente_id } });
                    setHistCred(data);
                  } catch { setHistCred([]); }
                }}>
                Ver crédito
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Filtros rápidos ── */}
      <div style={{display:'flex',gap:8,padding:'7px 12px',background:'#f5f5f5',borderBottom:'1px solid #ddd',flexWrap:'wrap',alignItems:'center'}}>
        <label style={{fontSize:13,fontWeight:600}}>Status:</label>
        {['aberto','vencido','recebido','todos'].map(st=>(
          <button key={st}
            className={`${s.btnErp} ${filtros.status===st?s.btnErpPrimario:''}`}
            style={{fontSize:13,padding:'4px 12px'}}
            onClick={()=>handleFiltro({status:st})}>
            {st.charAt(0).toUpperCase()+st.slice(1)}
          </button>
        ))}
        <div style={{marginLeft:8,display:'flex',gap:4,alignItems:'center'}}>
          {/* Com status=Recebido este período passa a filtrar pela DATA DE
              RECEBIMENTO (não vencimento) — fixo, sem seletor. Nos demais
              status (aberto/vencido/todos), o mesmo par de campos filtra
              por vencimento (padrão, comportamento de sempre) ou emissão,
              conforme o seletor abaixo. */}
          {filtros.status==='recebido' ? (
            <label style={{fontSize:13}}>Recebido de</label>
          ) : (
            <select className={s.inputErp} style={{height:26,fontSize:13,padding:'0 4px'}}
              value={filtros.tipo_data} onChange={e=>handleFiltro({tipo_data:e.target.value})}
              title="Escolhe se o período abaixo filtra pela data de vencimento ou de emissão do título">
              <option value="vencimento">Vencimento</option>
              <option value="emissao">Emissão</option>
            </select>
          )}
          <input key={`de${chaveDatas}`} type="date" className={s.inputErp}
            style={{width:130,height:26,fontSize:13,...(datasInvalidas.de?{borderColor:'#c62828',background:'#ffebee'}:{})}}
            value={filtros.venc_de} onChange={e=>mudarData('venc_de', e)} />
          <label style={{fontSize:13}}>até</label>
          <input key={`ate${chaveDatas}`} type="date" className={s.inputErp}
            style={{width:130,height:26,fontSize:13,...(datasInvalidas.ate?{borderColor:'#c62828',background:'#ffebee'}:{})}}
            value={filtros.venc_ate} onChange={e=>mudarData('venc_ate', e)} />
          {(datasInvalidas.de || datasInvalidas.ate) && (
            <span style={{fontSize:12,color:'#c62828',fontWeight:600}}
              title="Ex.: setembro, abril, junho e novembro têm 30 dias; fevereiro, 28 ou 29">
              ⚠ Data inválida no período
            </span>
          )}
          <button className={s.btnErp} style={{fontSize:13,padding:'4px 12px'}}
            onClick={()=>{ const ok={de:false,ate:false}; setDatasInvalidas(ok); setChaveDatas(k=>k+1);
              handleFiltro({venc_de:'',venc_ate:'',cliente_id:''}, ok); }}>Limpar</button>
        </div>
        <button className={s.btnErp} style={{fontSize:13,padding:'4px 12px'}}
          onClick={marcarTodos} disabled={elegiveisVisiveis.length===0}
          title="Mesma marcação do F12, aplicada de uma vez a todos os títulos elegíveis visíveis na grade">
          {todosMarcados ? '☐ Desmarcar todos' : '☑ Marcar todos'}
        </button>
        <label style={{fontSize:13,display:'flex',alignItems:'center',gap:4,marginLeft:8}}>
          <input type="checkbox" checked={filtros.mostrar_cartoes}
            onChange={e=>handleFiltro({mostrar_cartoes:e.target.checked})} />
          💳 Mostrar cartões (aguard. baixa)
        </label>
        <label style={{fontSize:13,display:'flex',alignItems:'center',gap:4,marginLeft:8}}
          title="Mostra também os títulos das outras lojas a que você tem acesso (só consulta — para receber, entre na loja do título)">
          <input type="checkbox" checked={filtros.consolidar}
            onChange={e=>handleFiltro({consolidar:e.target.checked})} />
          🏬 Consolidar lojas
        </label>
        <label style={{fontSize:13,display:'flex',alignItems:'center',gap:4,marginLeft:8}}
          title="Só os títulos cujo boleto/nota ainda não foi mandado ao cliente (nem por e-mail nem por WhatsApp)">
          <input type="checkbox" checked={filtros.so_nao_enviados}
            onChange={e=>handleFiltro({so_nao_enviados:e.target.checked})} />
          📭 Só não enviados
        </label>
        {outrasLojas.length > 0 && (
          <button className={s.btnErp}
            style={{fontSize:12,padding:'3px 8px',background:'#fff8e1',borderColor:'#f9a825',color:'#8d6e00'}}
            title={outrasLojas.map(l => `${l.loja_nome}: ${l.qtd} título(s) — ${fmtMoeda(l.total)}`).join('\n') + '\n\nClique para consolidar'}
            onClick={()=>handleFiltro({consolidar:true})}>
            ⚠ Cliente tem {outrasLojas.reduce((a,l)=>a+l.qtd,0)} título(s) em aberto em outra(s) loja(s)
            {' — '}{fmtMoeda(outrasLojas.reduce((a,l)=>a+parseFloat(l.total||0),0))}
          </button>
        )}
        <div style={{display:'flex',gap:4,alignItems:'center'}}>
          <label style={{fontSize:13}}>Cliente</label>
          <Autocomplete
            lista={clientes}
            value={filtros.cliente_id}
            onChange={v => handleFiltro({cliente_id:v})}
            placeholder="Código, nome, nome fantasia ou CPF/CNPJ..."
            getLabelFn={c => `${c.codigo} — ${c.nome_fantasia || c.nome}`}
            getSearchFn={c => `${c.codigo||''} ${c.nome||''} ${c.nome_fantasia||''} ${c.cpf_cnpj||''}`}
            style={{minWidth:300}}
          />
        </div>
      </div>

      {/* ── Formulário ── */}
      <div ref={formRef} onKeyDown={navEnter}
        className={s.headerRow}
        style={{gap:8, padding:'8px 12px', flexWrap:'wrap',
          ...(editando ? {background:'#f0f7ff', borderBottom:'2px solid #1976d2', boxShadow:'inset 0 -2px 0 #1565c0'} : {})}}>

        {/* Linha 1 */}
        <div style={{display:'flex',gap:8,width:'100%',alignItems:'flex-end',flexWrap:'wrap'}}>
          <div style={{display:'flex',flexDirection:'column',gap:3,width:60}}>
            <label className={s.labelErp} style={lSt}>Código</label>
            <input className={s.inputErp} readOnly value={form.id||''} style={{...iSt, background:'#f0f0f0'}} />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:3,flex:1,minWidth:260}}>
            <label className={s.labelErp} style={lSt}>Descrição *</label>
            <input ref={primeiroRef} className={s.inputErp} value={form.descricao||''}
              onChange={e=>set('descricao',e.target.value)} readOnly={!editando}
              style={{...iSt, ...(editando ? {borderColor:'#0d47a1', borderWidth:2} : {})}} />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:3,width:130}}>
            <label className={s.labelErp} style={lSt}>Nº Documento</label>
            <input className={s.inputErp} value={form.numero_documento||''}
              onChange={e=>set('numero_documento',e.target.value)} readOnly={!editando} style={iSt} />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:3,width:60}}>
            <label className={s.labelErp} style={lSt}>Parcela</label>
            <input className={s.inputErp} type="number" min="1" value={form.parcela||''}
              onChange={e=>set('parcela',e.target.value)} readOnly={!editando} style={iSt} />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:3,width:60}}>
            <label className={s.labelErp} style={lSt}>de</label>
            <input className={s.inputErp} type="number" min="1" value={form.total_parcelas||''}
              onChange={e=>set('total_parcelas',e.target.value)} readOnly={!editando} style={iSt} />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:3,width:110}}>
            <label className={s.labelErp} style={lSt}>Valor R$ *</label>
            <input className={s.inputErp} type="number" step="0.01" value={form.valor||''}
              onChange={e=>set('valor',e.target.value)} readOnly={!editando}
              style={{...iSt, textAlign:'right', fontWeight:700,
                ...(editando ? {fontSize:15, borderColor:'#0d47a1', borderWidth:2} : {})}} />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:3}}>
            <label className={s.labelErp} style={lSt}>Status</label>
            <span className={`${s.badge} ${stLabel.cls}`} style={{alignSelf:'flex-start',marginTop:4}}>{stLabel.txt}</span>
          </div>
          {form.status === 'recebido' &&
           (parseFloat(form.desconto_recebimento) > 0.004 || parseFloat(form.juros_recebimento) > 0.004) && (
            <div style={{display:'flex',gap:16,alignItems:'flex-end',fontSize:12,
              background:'#f8fafc',border:'1px solid #e2e8f0',borderRadius:4,padding:'4px 10px'}}>
              <div><span style={{color:'#666'}}>Original:</span> <strong>{fmtMoeda(form.valor)}</strong></div>
              {parseFloat(form.desconto_recebimento) > 0.004 && (
                <div style={{color:'#b91c1c'}}>Desconto: <strong>-{fmtMoeda(form.desconto_recebimento)}</strong></div>
              )}
              {parseFloat(form.juros_recebimento) > 0.004 && (
                <div style={{color:'#166534'}}>Juros: <strong>+{fmtMoeda(form.juros_recebimento)}</strong></div>
              )}
              <div><span style={{color:'#666'}}>Líquido recebido:</span> <strong>{fmtMoeda(form.valor_liquido_recebido)}</strong></div>
            </div>
          )}
        </div>

        {/* Linha 2 */}
        <div style={{display:'flex',gap:8,width:'100%',alignItems:'flex-end',flexWrap:'wrap'}}>
          <div style={{display:'flex',flexDirection:'column',gap:3,width:90}}>
            <label className={s.labelErp} style={lSt}>Cód. Cliente</label>
            <input className={s.inputErp} readOnly style={iSt}
              value={form.cliente_id
                ? (clientes.find(c=>String(c.id)===String(form.cliente_id))?.codigo ?? form.cliente_codigo ?? '—')
                : '—'} />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:3,flex:1,minWidth:220}}>
            <label className={s.labelErp} style={lSt}>Cliente</label>
            {editando ? (
              <Autocomplete
                lista={clientes}
                value={form.cliente_id||''}
                onChange={v => set('cliente_id', v)}
                placeholder="Código, nome, nome fantasia ou CPF/CNPJ..."
                getLabelFn={c => `${c.codigo} — ${c.nome_fantasia || c.nome}`}
                getSearchFn={c => `${c.codigo||''} ${c.nome||''} ${c.nome_fantasia||''} ${c.cpf_cnpj||''}`}
                style={{width:'100%'}} inputStyle={iSt}
              />
            ) : (
              <input className={s.inputErp} readOnly style={iSt}
                value={form.cliente_id
                  ? (clientes.find(c=>String(c.id)===String(form.cliente_id))?.nome || form.cliente_nome || '—')
                  : '—'} />
            )}
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:3,width:130}}>
            <label className={s.labelErp} style={lSt}>Emissão</label>
            <input className={s.inputErp} type="date" value={form.data_emissao||''}
              onChange={e=>set('data_emissao',e.target.value)} readOnly={!editando} style={iSt} />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:3,width:130}}>
            <label className={s.labelErp} style={lSt}>Vencimento *</label>
            <input className={s.inputErp} type="date" value={form.data_vencimento||''}
              onChange={e=>set('data_vencimento',e.target.value)} readOnly={!editando}
              style={{...iSt, ...(stEf==='vencido' ? {borderColor:'#e53935',color:'#b71c1c'} : editando ? {borderColor:'#0d47a1',borderWidth:2} : {})}} />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:3,width:130}}>
            <label className={s.labelErp} style={lSt}>Recebimento</label>
            <input className={s.inputErp} type="date" value={form.data_recebimento||''}
              onChange={e=>set('data_recebimento',e.target.value)} readOnly={!editando}
              style={{...iSt, ...(form.data_recebimento ? {color:'#2e7d32'} : {})}} />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:3,flex:1,minWidth:180}}>
            <label className={s.labelErp} style={lSt}>Conta Financeira</label>
            <select className={s.selectErp} value={form.conta_id||''} disabled={!editando}
              onChange={e=>set('conta_id',e.target.value)}
              style={editando ? {fontSize:14, height:30, borderColor:'#1976d2'} : {}}>
              <option value="">— Selecione —</option>
              {contasFinanc.map(c=><option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>
        </div>

        {/* Linha 3 — Categoria + Observações */}
        <div style={{display:'flex',gap:8,width:'100%',alignItems:'flex-end',flexWrap:'wrap'}}>
          <div style={{display:'flex',flexDirection:'column',gap:3,flex:1,minWidth:280}}>
            <label className={s.labelErp} style={lSt}>Categoria (Plano de Contas)</label>
            {editando ? (
              <Autocomplete
                lista={planoContas}
                value={form.plano_conta_id||''}
                onChange={v => set('plano_conta_id', v)}
                placeholder="Buscar por código ou descrição..."
                getLabelFn={pc => `${pc.codigo} — ${pc.descricao}`}
                getSearchFn={pc => `${pc.codigo} ${pc.descricao}`}
                style={{width:'100%'}} inputStyle={iSt}
              />
            ) : (
              <input className={s.inputErp} readOnly
                value={form.plano_conta_id
                  ? (planoContas.find(pc=>String(pc.id)===String(form.plano_conta_id))
                      ? `${planoContas.find(pc=>String(pc.id)===String(form.plano_conta_id)).codigo} — ${planoContas.find(pc=>String(pc.id)===String(form.plano_conta_id)).descricao}`
                      : form.plano_codigo ? `${form.plano_codigo} — ${form.plano_descricao}` : '—')
                  : '—'}
                style={{color: form.plano_conta_id ? '#1565c0' : '#888'}}
              />
            )}
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:3,flex:2,minWidth:200}}>
            <label className={s.labelErp} style={lSt}>Observações</label>
            <input className={s.inputErp} value={form.observacoes||''}
              onChange={e=>set('observacoes',e.target.value)} readOnly={!editando} style={iSt} />
          </div>
        </div>
      </div>

      {/* ── Mini-lista (grid lateral) — Ctrl+G foca, ↑/↓ navegam, F12 marca p/ lote ── */}
      <div ref={gradeRef} tabIndex={0}
        title="Ctrl+G foca a grade · ↑/↓ selecionam o documento · Ctrl+R abre o Receber · F12 marca/desmarca p/ baixa ou estorno em lote"
        style={{flex:1,overflowY:'auto',margin:'0 12px 4px',outline:'none'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead style={{position:'sticky',top:0,background:'#dde3ef',zIndex:1}}>
            <tr>
              <th style={{padding:'3px 6px',border:'1px solid #c0c8d8',width:26}} title="F12 marca/desmarca o registro selecionado"></th>
              <th style={{padding:'3px 6px',border:'1px solid #c0c8d8',width:50,textAlign:'left'}}>Cód.</th>
              {filtros.consolidar && <th style={{padding:'3px 6px',border:'1px solid #c0c8d8',width:140,textAlign:'left'}}>Loja</th>}
              <th style={{padding:'3px 6px',border:'1px solid #c0c8d8',textAlign:'left'}}>Descrição</th>
              <th style={{padding:'3px 6px',border:'1px solid #c0c8d8',width:60}}>Cód. Cliente</th>
              <th style={{padding:'3px 6px',border:'1px solid #c0c8d8',textAlign:'left'}}>Cliente</th>
              <th style={{padding:'3px 6px',border:'1px solid #c0c8d8',width:90}}>Emissão</th>
              <th style={{padding:'3px 6px',border:'1px solid #c0c8d8',width:90}}>Vencimento</th>
              <th style={{padding:'3px 6px',border:'1px solid #c0c8d8',width:90}}>Recebimento</th>
              <th style={{padding:'3px 6px',border:'1px solid #c0c8d8',width:110}}>Forma Pagto</th>
              <th style={{padding:'3px 6px',border:'1px solid #c0c8d8',width:110,textAlign:'right'}}>
                {filtros.status==='recebido' ? 'Valor Pago' : 'Valor'}
              </th>
              <th style={{padding:'3px 6px',border:'1px solid #c0c8d8',width:80}}>Status</th>
              <th style={{padding:'3px 6px',border:'1px solid #c0c8d8',width:64}} title="Boleto/nota enviado ao cliente: ✉ e-mail · 💬 WhatsApp">Enviado</th>
            </tr>
          </thead>
          <tbody>
            {carregando&&<tr><td colSpan={filtros.consolidar?13:12} style={{textAlign:'center',padding:10,color:'#888'}}>Carregando...</td></tr>}
            {!carregando&&lista.length===0&&<tr><td colSpan={filtros.consolidar?13:12} style={{textAlign:'center',padding:10,color:'#888',fontStyle:'italic'}}>
              {(datasInvalidas.de || datasInvalidas.ate) ? 'Corrija a data do período para filtrar.' : 'Nenhuma conta encontrada.'}</td></tr>}
            {lista.map((c,i)=>{
              const stKey = statusEfetivo(c);
              const st = STATUS_LABEL[stKey] || STATUS_LABEL.aberto;
              const ativo = form.id===c.id;
              // Marcação (F12/checkbox) serve tanto pra baixar em lote (título
              // aberto) quanto pra estornar em lote (título recebido, exceto
              // via Baixa de Cartões — esse se desfaz cancelando a baixa).
              const elegivelBaixa   = c.status === 'aberto' && !ehCartaoPendente(c);
              const elegivelEstorno = c.status === 'recebido' && !c.baixa_id;
              const elegivelMarcar  = elegivelParaMarcar(c);
              const valorExibido = filtros.status==='recebido' ? (c.valor_liquido_recebido ?? c.valor) : c.valor;
              return (
                <tr key={c.id} ref={el => { rowRefs.current[i] = el; }}
                  style={{cursor:'pointer', color: COR_STATUS[stKey]||'inherit',
                    background: ativo?'#cce3ff': c.outra_loja ? '#f3f0e6' : i%2===0?'#fff':'#f8f9fc',
                    ...(c.outra_loja ? {fontStyle:'italic'} : {})}}
                  title={c.outra_loja ? `Título da loja "${c.loja_nome}" — só consulta` : undefined}
                  onClick={async()=>{ setPosicao(i); await abrirLinha(c); }}
                >
                  <td style={{padding:'2px 6px',border:'1px solid #e0e4ec',textAlign:'center'}}
                    onClick={e=>e.stopPropagation()}>
                    <input type="checkbox" checked={marcados.has(c.id)} disabled={!elegivelMarcar}
                      title={elegivelBaixa ? 'Marcar para baixa em lote'
                        : elegivelEstorno ? 'Marcar para estornar em lote'
                        : 'Não elegível pra lote (cancelado, cartão pendente, ou recebido via Baixa de Cartões)'}
                      onChange={e=>marcar(c.id, e.target.checked)} />
                  </td>
                  <td style={{padding:'2px 6px',border:'1px solid #e0e4ec'}}>{c.id}</td>
                  {filtros.consolidar && (
                    <td style={{padding:'2px 6px',border:'1px solid #e0e4ec',fontWeight:c.outra_loja?600:'normal'}}>{c.loja_nome}</td>
                  )}
                  <td style={{padding:'2px 6px',border:'1px solid #e0e4ec'}}>{c.descricao}</td>
                  <td style={{padding:'2px 6px',border:'1px solid #e0e4ec',textAlign:'center'}}>{c.cliente_codigo ?? '—'}</td>
                  <td style={{padding:'2px 6px',border:'1px solid #e0e4ec'}}>{c.cliente_nome||'—'}</td>
                  <td style={{padding:'2px 6px',border:'1px solid #e0e4ec',textAlign:'center'}}>{fmtData(c.data_emissao)}</td>
                  <td style={{padding:'2px 6px',border:'1px solid #e0e4ec',textAlign:'center'}}>{fmtData(c.data_vencimento)}</td>
                  <td style={{padding:'2px 6px',border:'1px solid #e0e4ec',textAlign:'center',color:c.data_recebimento?'#2e7d32':'inherit',fontWeight:filtros.status==='recebido'?600:'normal'}}>{fmtData(c.data_recebimento)}</td>
                  <td style={{padding:'2px 6px',border:'1px solid #e0e4ec',textAlign:'center'}}>
                    {c.forma_recebimento
                      ? <span title={c.status==='aberto' ? 'Cartão aguardando Baixa de Cartões' : ''}>
                          {c.forma_recebimento}{c.status==='aberto' ? ' ⏳' : ''}
                        </span>
                      : '—'}
                  </td>
                  <td style={{padding:'2px 6px',border:'1px solid #e0e4ec',textAlign:'right',fontWeight:600}}>{fmtMoeda(valorExibido)}</td>
                  <td style={{padding:'2px 6px',border:'1px solid #e0e4ec'}}>
                    <span className={`${s.badge} ${st.cls}`}>{st.txt}</span>
                  </td>
                  <td style={{padding:'2px 4px',border:'1px solid #e0e4ec',textAlign:'center',whiteSpace:'nowrap',fontSize:14}}>
                    <span style={{opacity:c.email_enviado_em?1:0.18,filter:c.email_enviado_em?'none':'grayscale(1)'}}
                      title={c.email_enviado_em ? `E-mail enviado em ${fmtDataHora(c.email_enviado_em)}${c.email_destino ? ` para ${c.email_destino}` : ''}` : 'E-mail não enviado'}>✉️</span>
                    {' '}
                    <span style={{opacity:c.whatsapp_em?1:0.18,filter:c.whatsapp_em?'none':'grayscale(1)'}}
                      title={c.whatsapp_em ? `WhatsApp aberto com a mensagem em ${fmtDataHora(c.whatsapp_em)}${c.whatsapp_destino ? ` (${c.whatsapp_destino})` : ''} — o envio é confirmado por você dentro do WhatsApp` : 'WhatsApp não enviado'}>💬</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {lista.length>0&&(
            <tfoot>
              <tr style={{background:'#eef2fa',fontWeight:600}}>
                <td colSpan={filtros.consolidar?9:8} style={{padding:'3px 6px',border:'1px solid #c0c8d8',textAlign:'right',fontSize:12}}>Total filtrado:</td>
                <td style={{padding:'3px 6px',border:'1px solid #c0c8d8',textAlign:'right',fontSize:12}}>
                  {fmtMoeda(totalListaAtual)}
                </td>
                <td style={{border:'1px solid #c0c8d8'}}></td>
                <td style={{border:'1px solid #c0c8d8'}}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* ── Status bar ── */}
      <div className={s.statusBar}>
        {carregando ? 'Carregando...' : `${total} registro(s)`}
        {!carregando && total > 0 && (
          <span style={{marginLeft:12, fontWeight:700, color:'#1565c0'}}>
            {clienteFiltrado
              ? `💰 Total de ${clienteFiltrado.nome}: ${fmtMoeda(totalListaAtual)}`
              : `Total: ${fmtMoeda(totalListaAtual)}`}
          </span>
        )}
        {erroMsg && <span className={s.statusErro} style={{marginLeft:12}}>{erroMsg}</span>}
        {marcados.size > 0 && (
          <span style={{marginLeft:12, fontWeight:700, color:'#166534'}}>
            ☑ {marcados.size} marcado(s) — {fmtMoeda(titulosMarcados.reduce((a,c)=>a+parseFloat(c.valor||0),0))}
          </span>
        )}
      </div>

      {loteEmail && !loteEmail.enviando && (
        <div className={s.overlay} onMouseDown={e => e.target === e.currentTarget && setLoteEmail(null)}>
          <div className={s.modal} style={{ width: 620, maxWidth: '95vw' }}>
            <div className={s.modalTitle}>📧 Envio de e-mails — {loteEmail.resultados.filter(r => r.ok).length} de {loteEmail.resultados.length} enviado(s)</div>
            <div className={s.modalBody} style={{ maxHeight: '60vh', overflow: 'auto', fontSize: 13 }}>
              {loteEmail.erro && <div style={{ color: '#c62828', marginBottom: 8 }}>{loteEmail.erro}</div>}
              {loteEmail.resultados.map(r => {
                const t = lista.find(c => c.id === r.id);
                return (
                  <div key={r.id} style={{ padding: '4px 0', borderBottom: '1px solid #eee', color: r.ok ? '#1b7a3d' : '#c62828' }}>
                    <strong>#{r.id} {t?.cliente_nome || ''}</strong>: {r.ok ? `enviado para ${r.enviado_para}` : r.erro}
                  </div>
                );
              })}
            </div>
            <div className={s.modalFooter}><button className={s.btnErpPrimario} onClick={() => setLoteEmail(null)}>Fechar</button></div>
          </div>
        </div>
      )}

      {filaWhats && (
        <div className={s.overlay}>
          <div className={s.modal} style={{ width: 680, maxWidth: '95vw' }}>
            <div className={s.modalTitle}>💬 WhatsApp em fila — {filaWhats.filter(x => x.estado === 'aberto').length} de {filaWhats.length} aberto(s)</div>
            <div className={s.modalBody} style={{ maxHeight: '60vh', overflow: 'auto', fontSize: 13 }}>
              <div style={{ color: '#555', marginBottom: 8 }}>
                Clique em <b>Abrir</b>: o WhatsApp abre com a mensagem pronta. Aperte <b>Enviar</b> lá, volte aqui e abra o próximo.
              </div>
              {filaWhats.map((x, i) => (
                <div key={x.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0', borderBottom: '1px solid #eee' }}>
                  <span style={{ width: 22, textAlign: 'right', color: '#888' }}>{i + 1}.</span>
                  <span style={{ flex: 1 }}>
                    <strong>{x.cliente || '—'}</strong> · #{x.id} · {fmtMoeda(x.valor)} · venc. {fmtData(x.vencimento)}
                    {x.msg && <div style={{ fontSize: 11.5, color: x.estado === 'erro' ? '#c62828' : x.estado === 'aberto' ? '#1b7a3d' : '#6b7280' }}>{x.msg}</div>}
                  </span>
                  <button className={x.estado === 'pendente' ? s.btnErpPrimario : s.btnErp} onClick={() => abrirWhatsDaFila(x)}>
                    {x.estado === 'aberto' || x.estado === 'ja' ? 'Abrir de novo' : 'Abrir'}
                  </button>
                </div>
              ))}
            </div>
            <div className={s.modalFooter} style={{ justifyContent: 'space-between' }}>
              <label style={{ fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 6 }}>
                Abrir em:
                <select value={modoWhats} onChange={e => { setModoWhats(e.target.value); salvarModoWhatsApp(e.target.value || null); }}>
                  <option value="">perguntar</option>
                  <option value="app">App instalado no computador</option>
                  <option value="web">WhatsApp Web (sempre a mesma aba)</option>
                </select>
              </label>
              <button className={s.btnErp} onClick={() => { setFilaWhats(null); carregarLista(); }}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Footer ── */}
      <div className={s.footer}>
        <div className={s.footerLeft}>
          <button className={s.btnErpNav} disabled={editando||posicao===0} onClick={()=>navPara(0)}>|◄</button>
          <button className={s.btnErpNav} disabled={editando||posicao===0} onClick={()=>navPara(posicao-1)}>◄</button>
          <span style={{fontSize:12,margin:'0 4px'}}>{lista.length?posicao+1:0} / {total}</span>
          <button className={s.btnErpNav} disabled={editando||posicao>=total-1} onClick={()=>navPara(posicao+1)}>►</button>
          <button className={s.btnErpNav} disabled={editando||posicao>=total-1} onClick={()=>navPara(total-1)}>►|</button>
        </div>
        <div className={s.footerRight}>
          {marcados.size > 0 && !editando && (<>
            <button className={s.btnErp} onClick={abrirFilaWhatsapp}
              title="Abre uma fila com os títulos marcados: um clique por cliente abre o WhatsApp com a mensagem pronta">
              💬 WhatsApp ({marcados.size})
            </button>
            <button className={s.btnErp} onClick={enviarEmailMarcados} disabled={loteEmail?.enviando}
              title="Envia o boleto/nota de cada título marcado para o e-mail do cliente">
              {loteEmail?.enviando ? 'Enviando e-mails...' : `📧 E-mail (${marcados.size})`}
            </button>
            <button className={s.btnErp} onClick={imprimirMarcados}
              title="Imprimir a lista dos documentos marcados (cód., descrição, cliente, emissão, vencimento e valor, com total)">
              🖨️ Imprimir Marcados
            </button>
            {titulosParaBaixa.length > 0 && (
              <button className={`${s.btnErp} ${s.btnErpPrimario}`} onClick={()=>setModalBaixaLote(true)}
                title="Baixar todos os títulos abertos marcados de uma vez (F12 marca/desmarca na grade)">
                💰 Baixar {titulosParaBaixa.length} selecionado(s)
              </button>
            )}
            {titulosParaEstorno.length > 0 && (
              <button className={s.btnErp} style={{color:'#b45309'}} onClick={handleEstornarLote}
                title="Estorna todos os títulos recebidos marcados de uma vez — corrige uma baixa em lote feita por engano (F12 marca/desmarca na grade)">
                ↩ Estornar {titulosParaEstorno.length} selecionado(s)
              </button>
            )}
          </>)}
          {podeReceber && (
            <button className={`${s.btnErp} ${s.btnErpPrimario}`} onClick={()=>setModalReceber(true)}
              title="Receber o documento selecionado (Ctrl+R)">
              ✅ Receber <span style={{opacity:.75,fontWeight:400,fontSize:11}}>Ctrl+R</span>
            </button>
          )}
          {cartaoPendente && !editando && (
            <span style={{fontSize:11,color:'#1e40af',background:'#eff6ff',border:'1px solid #93c5fd',
              borderRadius:4,padding:'4px 8px',alignSelf:'center'}}
              title="Esta conta só pode ser liquidada na tela Baixa de Cartões (ou estornada lá pelo botão ↩)">
              💳 Aguardando Baixa de Cartões
            </span>
          )}
          {form.id && form.status==='recebido' && !editando && (<>
            <button className={s.btnErp} disabled={imprimindoRecibo} onClick={()=>imprimirRecibo('termica')}
              title="Imprimir recibo de pagamento — térmica">
              🖨️ Recibo Térmica
            </button>
            <button className={s.btnErp} disabled={imprimindoRecibo} onClick={()=>imprimirRecibo('a4')}
              title="Imprimir recibo de pagamento — A4">
              📄 Recibo A4
            </button>
            <button className={s.btnErp} style={{color:'#b45309'}} onClick={handleEstornarRecebimento}
              title="Desfaz o recebimento: a conta volta para aberto e o saldo é revertido">
              ↩ Estornar Receb.
            </button>
          </>)}
          {podeCancelarStatus && (
            <button className={`${s.btnErp} ${s.btnErpPerigo}`} onClick={async()=>{
              if (!window.confirm('Cancelar esta conta?')) return;
              const senha = await pedirSenhaAdmin();
              if (senha === null) return;
              try {
                await api.put(`/financeiro/contas-receber/${form.id}`, { ...form, status:'cancelado', senha_admin:senha, valor:parseFloat(form.valor), cliente_id:form.cliente_id||null, conta_id:form.conta_id||null });
                await abrirConta(form.id); carregarLista(); carregarResumo();
              } catch(err){ setErroMsg(err.response?.data?.erro||'Erro.'); }
            }}>Cancelar</button>
          )}
          <button className={`${s.btnErp} ${s.btnErpPrimario}`} onClick={handleNovo} disabled={editando}>Novo</button>
          <button className={s.btnErp} onClick={()=>setModalBusca(true)} disabled={editando}>Buscar</button>
          <button className={s.btnErp} onClick={handleAlterar} disabled={editando||!form.id||stEf==='recebido'||stEf==='cancelado'}>Alterar</button>
          <button className={s.btnErp} onClick={handleCancelar} disabled={!editando}>Cancelar</button>
          <button className={`${s.btnErp} ${s.btnErpPrimario}`} onClick={handleSalvar} disabled={!editando||salvando}>
            {salvando?'Salvando...':'Salvar'}
          </button>
          <button className={`${s.btnErp} ${s.btnErpPerigo}`} onClick={handleExcluir}
            disabled={editando||!form.id||stEf==='recebido'}>Excluir</button>
          {/* Mesmos botões de documento fiscal que o F7 (Buscar Movimentação
              de Caixa, no PDV) já oferece pra uma venda — aqui a partir do
              título selecionado nesta tela, usando o resumo por venda que
              o próprio F7 usa (GET /vendas?codigo=) e o boleto vinculado
              a este conta_receber_id. */}
          {form.id && !editando && docFiscalCr?.doc_fiscal_status === 'autorizada'
            && (docFiscalCr.doc_fiscal_tipo === 'nfce' || docFiscalCr.doc_fiscal_tipo === 'nfe') && (
            <button className={s.btnErp}
              onClick={() => abrirDanfeNfePdf(`/fiscal/nfe/${docFiscalCr.doc_fiscal_id}/danfe`)}
              title={`Abrir o DANFE da ${docFiscalCr.doc_fiscal_tipo === 'nfce' ? 'NFC-e' : 'NF-e'} nº ${docFiscalCr.doc_fiscal_numero} vinculada a esta venda`}>
              📄 DANFE {docFiscalCr.doc_fiscal_tipo === 'nfce' ? 'NFC-e' : 'NF-e'}
            </button>
          )}
          {form.id && !editando && docFiscalCr?.doc_fiscal_status === 'autorizada' && docFiscalCr.doc_fiscal_tipo === 'nfse' && (
            <button className={s.btnErp}
              onClick={() => abrirDanfseOficial({ documento_id: docFiscalCr.doc_fiscal_id, sucesso: true }, {})}
              title={`Abrir o DANFSe da NFS-e nº ${docFiscalCr.doc_fiscal_numero} vinculada a esta venda`}>
              🧾 DANFSe
            </button>
          )}
          {form.id && !editando && boletoCr && (
            <button className={s.btnErp} onClick={() => abrirBoleto(boletoCr)}
              title="Abrir o boleto deste título (página do banco, com QR code, quando integrado — senão o PDF padrão)">
              🏦 Ver Boleto
            </button>
          )}
          {form.id && !editando && (boletoCr || docFiscalCr?.doc_fiscal_status === 'autorizada') && (<>
            <button className={s.btnErp} onClick={handleEnviarWhatsapp}
              title="Abre o WhatsApp com uma mensagem pronta (link do boleto/nota) pro número cadastrado do cliente — você confere e aperta Enviar lá dentro">
              💬 WhatsApp
            </button>
            <button className={s.btnErp} onClick={handleEnviarEmailCr} disabled={enviandoEmailCr}
              title="Envia o boleto/DANFE/DANFSe (PDF) por e-mail pro cliente">
              {enviandoEmailCr ? 'Enviando...' : '📧 E-mail'}
            </button>
          </>)}
          <button className={s.btnErp} onClick={()=>setModalBoletos(true)} disabled={editando}
            title="Gerar boletos, remessa e retorno CNAB400">🏦 Boletos</button>
          <div className={s.footerSep}/>
          <button className={s.btnErp} onClick={()=>window.history.back()}>Sair</button>
        </div>
      </div>

      {/* ── Modais ── */}
      {modalBoletos && (
        <ModalBoletos onClose={()=>{ setModalBoletos(false); carregarLista(); carregarResumo(); }} />
      )}
      {modalNovo && (
        <ModalNovoTituloParcelado
          tipo="receber"
          entidades={clientes}
          contasFinanc={contasFinanc}
          planoContas={planoContas}
          onSalvo={handleNovoSalvo}
          onClose={() => setModalNovo(false)}
        />
      )}
      {modalBusca && (
        <ModalBusca
          onClose={()=>setModalBusca(false)}
          onSelect={async(c)=>{
            setModalBusca(false);
            const idx=lista.findIndex(x=>x.id===c.id);
            setPosicao(idx>=0?idx:0);
            await abrirConta(c.id);
            // Leva o cliente da busca para o filtro: a grade passa a mostrar só
            // os documentos dele, no status selecionado (aberto/vencido/recebido)
            if (c.cliente_id) handleFiltro({ cliente_id: String(c.cliente_id) });
          }}
        />
      )}
      {modalReceber && form.id && (
        <ModalReceber
          conta={form}
          contas={contasFinanc}
          bandeiras={bandeiras}
          orgData={orgData}
          onReceber={handleReceber}
          onClose={()=>setModalReceber(false)}
        />
      )}
      {modalBaixaLote && titulosParaBaixa.length > 0 && (
        <ModalBaixaLoteContaReceber
          titulos={titulosParaBaixa}
          contas={contasFinanc}
          orgData={orgData}
          onConfirmar={handleBaixarLote}
          onClose={()=>setModalBaixaLote(false)}
        />
      )}

      {/* ── Histórico do crédito do cliente (origem e usos) ── */}
      {modalCredito && (
        <div style={{position:'fixed',inset:0,background:'rgba(15,23,42,.5)',zIndex:999,
          display:'flex',alignItems:'center',justifyContent:'center'}}
          onMouseDown={e=>{ if (e.target===e.currentTarget) setModalCredito(false); }}>
          <div style={{background:'#fff',borderRadius:6,width:640,maxWidth:'94vw',maxHeight:'80vh',
            display:'flex',flexDirection:'column',boxShadow:'0 10px 40px rgba(0,0,0,.4)',overflow:'hidden'}}>
            <div style={{background:'#0f2557',color:'#fff',padding:'10px 14px',fontSize:13,fontWeight:700,
              display:'flex',justifyContent:'space-between'}}>
              <span>💳 Créditos de {form.cliente_nome || 'cliente'}</span>
              <button onClick={()=>setModalCredito(false)}
                style={{background:'none',border:'none',color:'#fff',cursor:'pointer',fontSize:15}}>✕</button>
            </div>
            <div style={{overflowY:'auto',padding:14}}>
              {histCred === null && <div style={{fontSize:13,color:'#666'}}>Carregando…</div>}
              {histCred?.length === 0 && <div style={{fontSize:13,color:'#666'}}>Nenhum crédito registrado para este cliente.</div>}
              {histCred?.map(c => (
                <div key={c.id} style={{border:'1px solid #e5e7eb',borderRadius:6,padding:'10px 12px',
                  marginBottom:10, opacity:c.cancelado?0.55:1}}>
                  <div style={{display:'flex',justifyContent:'space-between',flexWrap:'wrap',gap:6}}>
                    <div style={{fontSize:13}}>
                      <strong>Crédito #{c.id}</strong>
                      <span style={{color:'#666'}}> — {new Date(c.criado_em).toLocaleDateString('pt-BR')}</span>
                      {c.cancelado && <span style={{color:'#b91c1c',fontWeight:700}}> (cancelado)</span>}
                    </div>
                    <div style={{fontSize:13}}>
                      Valor: <strong>{fmtMoeda(c.valor)}</strong>
                      <span style={{marginLeft:10,color:parseFloat(c.saldo)>0?'#166534':'#888'}}>
                        Saldo: <strong>{fmtMoeda(c.saldo)}</strong>
                      </span>
                    </div>
                  </div>
                  <div style={{fontSize:12,color:'#374151',marginTop:4}}>
                    <strong>Origem:</strong>{' '}
                    {c.devolucao_id
                      ? `Devolução #${c.devolucao_id}${c.devolucao_venda_id ? ` da venda #${c.devolucao_venda_id}` : ''}`
                      : (c.motivo || '—')}
                  </div>
                  {c.usos.length > 0 ? (
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginTop:8}}>
                      <thead>
                        <tr style={{background:'#f3f4f6',textAlign:'left'}}>
                          <th style={{padding:'3px 8px'}}>Usado em</th>
                          <th style={{padding:'3px 8px'}}>Documento baixado</th>
                          <th style={{padding:'3px 8px',textAlign:'right'}}>Valor usado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {c.usos.map((u,i)=>(
                          <tr key={i} style={{borderTop:'1px solid #eee'}}>
                            <td style={{padding:'3px 8px'}}>{new Date(u.criado_em).toLocaleDateString('pt-BR')}</td>
                            <td style={{padding:'3px 8px'}}>
                              {u.conta_receber_id
                                ? `CR #${u.conta_receber_id} — ${u.conta_descricao}`
                                : `Pagamento na Venda #${u.uso_venda_id}`}
                            </td>
                            <td style={{padding:'3px 8px',textAlign:'right',fontWeight:600}}>{fmtMoeda(u.valor)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    !c.cancelado && <div style={{fontSize:12,color:'#888',marginTop:6}}>Ainda não utilizado.</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
